"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";

interface TestimonialFormProps {
  mentorId: string;
  mentorFirstName: string;
}

type FormState = "idle" | "submitting" | "success" | "error";

export function TestimonialForm({ mentorId, mentorFirstName }: TestimonialFormProps) {
  const t = useTranslations("testimonialForm");
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<FormState>("idle");
  const [authorName, setAuthorName] = useState("");
  const [quote, setQuote] = useState("");
  const [stars, setStars] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, handleKey]);

  function handleClose() {
    setOpen(false);
    setState("idle");
    setAuthorName("");
    setQuote("");
    setStars(0);
    setHovered(0);
    setErrorMsg("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState("submitting");
    setErrorMsg("");

    try {
      const res = await fetch("/api/testimonials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ author_name: authorName, quote, stars: stars > 0 ? stars : undefined, mentorId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? t("errorFallback"));
      }

      setState("success");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t("errorFallback"));
      setState("error");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm font-medium underline-offset-2 hover:underline"
        style={{ color: "var(--color-muted)" }}
      >
        {t("leaveReview")}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div
            className="w-full sm:max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-base font-semibold">{t("title")}</h2>
                <p className="mt-0.5 text-sm" style={{ color: "var(--color-muted)" }}>
                  {t("forMentor", { name: mentorFirstName })}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 rounded-md"
                style={{ color: "var(--color-subtle)" }}
                aria-label="Close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {state === "success" ? (
              <div className="py-8 text-center">
                <div
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                  style={{ background: "var(--color-surface)", color: "var(--color-brand)" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <p className="font-medium" style={{ color: "var(--color-text)" }}>
                  {t("successTitle")}
                </p>
                <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
                  {t("successText")}
                </p>
                <button
                  type="button"
                  onClick={handleClose}
                  className="mt-6 text-sm font-medium"
                  style={{ color: "var(--color-brand)" }}
                >
                  {t("close")}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium" style={{ color: "var(--color-muted)" }}>
                    {t("rating")}
                  </label>
                  <div className="flex items-center gap-1" role="group" aria-label={t("rating")}>
                    {Array.from({ length: 5 }).map((_, i) => {
                      const val = i + 1;
                      const filled = val <= (hovered || stars);
                      return (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setStars(val)}
                          onMouseEnter={() => setHovered(val)}
                          onMouseLeave={() => setHovered(0)}
                          aria-label={`${val} star${val !== 1 ? "s" : ""}`}
                          className="p-0.5 transition-transform hover:scale-110"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: filled ? "#eab308" : "var(--color-border)" }}>
                            <path d="M12 2.5l2.9 5.88 6.49.95-4.7 4.58 1.11 6.47L12 17.33l-5.8 3.05 1.11-6.47-4.7-4.58 6.49-.95L12 2.5z" />
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="testimonial-name" className="text-xs font-medium" style={{ color: "var(--color-muted)" }}>
                    {t("yourName")}
                  </label>
                  <input
                    id="testimonial-name"
                    type="text"
                    required
                    maxLength={120}
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 transition"
                    style={{ background: "var(--color-surface)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="testimonial-quote" className="text-xs font-medium" style={{ color: "var(--color-muted)" }}>
                    {t("yourReview")}
                  </label>
                  <textarea
                    id="testimonial-quote"
                    required
                    rows={5}
                    maxLength={2000}
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                    className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 transition resize-none"
                    style={{ background: "var(--color-surface)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  />
                  <p className="text-right text-xs" style={{ color: "var(--color-subtle)" }}>
                    {quote.length}/2000
                  </p>
                </div>

                <p className="text-xs" style={{ color: "var(--color-subtle)" }}>
                  {t("moderation")}
                </p>

                {state === "error" && (
                  <p className="text-xs" style={{ color: "var(--color-brand)" }}>
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={state === "submitting"}
                  className="mt-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                  style={{ background: "var(--color-brand)" }}
                >
                  {state === "submitting" ? t("sending") : t("submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
